'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// pdf.js v3 (UMD): renderiza o PDF em <canvas>, funcionando inclusive no mobile,
// onde <iframe>/<embed> de PDF não é exibido inline pelo navegador. Carregado por
// CDN sob demanda (sem adicionar dependência ao bundle / sem rebuild da imagem).
const PDFJS_VERSION = '3.11.174';
const PDFJS_LIB_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Carrega o PDF (autenticado) como Blob. Chamado ao abrir o modal. */
  fetchBlob: () => Promise<Blob>;
  /** Baixar como fallback (mesmo arquivo já carregado, sem nova requisição). */
  onDownload?: (blob: Blob) => void;
}

// Carrega o script da CDN uma única vez (singleton). Resolve quando window.pdfjsLib existe.
let pdfjsPromise: Promise<unknown> | null = null;
function carregarPdfjs(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  const w = window as unknown as { pdfjsLib?: unknown };
  if (w.pdfjsLib) return Promise.resolve(w.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_LIB_URL;
    script.async = true;
    script.onload = () => {
      const lib = (window as unknown as { pdfjsLib?: unknown }).pdfjsLib;
      if (lib) resolve(lib);
      else reject(new Error('pdf.js não inicializou'));
    };
    script.onerror = () => {
      pdfjsPromise = null;
      reject(new Error('Falha ao carregar o leitor de PDF'));
    };
    document.head.appendChild(script);
  });
  return pdfjsPromise;
}

export default function PdfViewerModal({
  open,
  onClose,
  title,
  fetchBlob,
  onDownload,
}: PdfViewerModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const blobRef = useRef<Blob | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [temBlob, setTemBlob] = useState(false);

  // Fecha com ESC e trava o scroll do body.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const renderizar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    setTemBlob(false);
    blobRef.current = null;
    try {
      const [lib, blob] = await Promise.all([carregarPdfjs(), fetchBlob()]);
      blobRef.current = blob;
      setTemBlob(true);
      const pdfjs = lib as {
        GlobalWorkerOptions: { workerSrc: string };
        getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfDocumentProxy> };
      };
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

      const data = await blob.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data }).promise;

      const container = containerRef.current;
      if (!container) return;
      container.replaceChildren();

      const dpr = window.devicePixelRatio || 1;
      const larguraAlvo = Math.min(container.clientWidth || 800, 1100);

      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const scale = larguraAlvo / base.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.className = 'mb-3 rounded bg-white shadow-sm';
        ctx.scale(dpr, dpr);
        container.appendChild(canvas);
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
      setCarregando(false);
    } catch (e) {
      const msg =
        e instanceof Error && e.message && !/load|carregar|pdf\.js/i.test(e.message)
          ? e.message
          : 'Não foi possível exibir o PDF aqui. Use o botão "Baixar" para abri-lo no seu dispositivo.';
      setErro(msg);
      setCarregando(false);
    }
  }, [fetchBlob]);

  useEffect(() => {
    if (!open) {
      // limpa canvases ao fechar p/ liberar memória
      containerRef.current?.replaceChildren();
      return;
    }
    // Efeito de sincronização com sistemas externos: baixa o PDF (rede) e o
    // desenha imperativamente em <canvas>. O setState inicial de loading dentro
    // de renderizar() é intencional (feedback tátil) e não gera cascata observável.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void renderizar();
  }, [open, renderizar]);

  if (!open) return null;

  const baixar = () => {
    if (blobRef.current && onDownload) onDownload(blobRef.current);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="mx-auto flex h-full w-full max-w-4xl flex-col bg-[var(--bg-subtle,#f3f4f6)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-3">
          <h3 className="truncate text-sm font-semibold text-gray-900 sm:text-base">{title}</h3>
          <div className="flex shrink-0 items-center gap-1">
            {onDownload && temBlob && (
              <button
                onClick={baixar}
                className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Baixar PDF"
                title="Baixar"
              >
                <i className="pi pi-download" aria-hidden="true" />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Fechar"
              title="Fechar"
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Corpo rolável com os canvases das páginas */}
        <div className="relative flex-1 overflow-auto p-3 sm:p-4">
          {carregando && (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              <i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />
              Carregando documento…
            </div>
          )}
          {erro && (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-gray-600">
              <i className="pi pi-exclamation-triangle text-2xl text-amber-500" aria-hidden="true" />
              <p>{erro}</p>
              {onDownload && temBlob && (
                <button
                  onClick={baixar}
                  className="rounded bg-[var(--brand,#2563eb)] px-4 py-2 text-sm font-medium text-white"
                >
                  Baixar PDF
                </button>
              )}
            </div>
          )}
          <div ref={containerRef} className={carregando || erro ? 'hidden' : 'mx-auto max-w-3xl'} />
        </div>
      </div>
    </div>
  );
}

interface PdfDocumentProxy {
  numPages: number;
  getPage: (n: number) => Promise<{
    getViewport: (params: { scale: number }) => { width: number; height: number };
    render: (params: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => {
      promise: Promise<void>;
    };
  }>;
}
